import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { getCoachee, getSession, updateSession } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Save, 
  Loader2, 
  Calendar, 
  Target,
  CheckSquare,
  TrendingUp,
  MessageCircle,
  Lightbulb,
  ClipboardCheck,
  Award,
  AlertCircle,
  HelpCircle,
  FileText,
  Hash
} from 'lucide-react';

export default function SessionEdit() {
  const { coacheeId, sessionId } = useParams();
  const navigate = useNavigate();
  const [coachee, setCoachee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    numero_sesion: 1,
    fecha: '',
    objetivo_proceso: '',
    bienvenida: '',
    revision_tareas: '',
    avances_objetivo: '',
    tema_sesion: '',
    estado_actual: '',
    estado_deseado: '',
    kpi_sesion: '',
    preguntas_significativas: '',
    tecnicas_utilizadas: '',
    tareas_proxima_sesion: '',
    conclusiones_aprendizajes: '',
    revision_kpi: '',
    aprendizajes_coach: '',
    fortaleza_desarrollada: '',
    area_oportunidad: '',
    preguntas_metacoach: ''
  });

  useEffect(() => {
    loadData();
  }, [coacheeId, sessionId]);

  const loadData = async () => {
    try {
      const [coacheeData, sessionData] = await Promise.all([
        getCoachee(coacheeId),
        getSession(coacheeId, sessionId)
      ]);
      setCoachee(coacheeData);
      setFormData({
        numero_sesion: sessionData.numero_sesion || 1,
        fecha: sessionData.fecha || '',
        objetivo_proceso: sessionData.objetivo_proceso || '',
        bienvenida: sessionData.bienvenida || '',
        revision_tareas: sessionData.revision_tareas || '',
        avances_objetivo: sessionData.avances_objetivo || '',
        tema_sesion: sessionData.tema_sesion || '',
        estado_actual: sessionData.estado_actual || '',
        estado_deseado: sessionData.estado_deseado || '',
        kpi_sesion: sessionData.kpi_sesion || '',
        preguntas_significativas: sessionData.preguntas_significativas || '',
        tecnicas_utilizadas: sessionData.tecnicas_utilizadas || '',
        tareas_proxima_sesion: sessionData.tareas_proxima_sesion || '',
        conclusiones_aprendizajes: sessionData.conclusiones_aprendizajes || '',
        revision_kpi: sessionData.revision_kpi || '',
        aprendizajes_coach: sessionData.aprendizajes_coach || '',
        fortaleza_desarrollada: sessionData.fortaleza_desarrollada || '',
        area_oportunidad: sessionData.area_oportunidad || '',
        preguntas_metacoach: sessionData.preguntas_metacoach || ''
      });
    } catch (error) {
      toast.error('Error al cargar datos');
      navigate(`/coachees/${coacheeId}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fecha) {
      toast.error('La fecha es requerida');
      return;
    }

    setSaving(true);
    try {
      await updateSession(coacheeId, sessionId, formData);
      toast.success('Sesión actualizada exitosamente');
      navigate(`/coachees/${coacheeId}/sessions/${sessionId}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || e.message || 'Error').join(', '));
      } else {
        toast.error('Error al actualizar sesión');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-10 h-10 animate-spin text-blue-900" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="session-edit-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-100 rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-heading text-3xl text-slate-800">
              Editar Sesión #{formData.numero_sesion}
            </h1>
            <p className="text-slate-500 mt-1">
              {coachee?.nombre}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="glass-card">
              <CardContent className="p-6">
                <Label htmlFor="numero_sesion" className="flex items-center gap-2 mb-2">
                  <Hash className="w-4 h-4 text-slate-400" />
                  Número de Sesión
                </Label>
                <Input
                  id="numero_sesion"
                  name="numero_sesion"
                  type="number"
                  min="1"
                  value={formData.numero_sesion}
                  onChange={handleChange}
                  className="glass-input text-2xl font-bold text-center"
                />
              </CardContent>
            </Card>

            <Card className="glass-card md:col-span-2">
              <CardContent className="p-6">
                <Label htmlFor="fecha" className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  Fecha de la Sesión
                </Label>
                <Input
                  id="fecha"
                  name="fecha"
                  type="date"
                  value={formData.fecha}
                  onChange={handleChange}
                  className="glass-input"
                />
              </CardContent>
            </Card>
          </div>

          {/* Objetivo */}
          <Card className="glass-card">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-heading text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-900" />
                Objetivo de Proceso
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <Textarea
                name="objetivo_proceso"
                value={formData.objetivo_proceso}
                onChange={handleChange}
                placeholder="Describe el objetivo general del proceso de coaching..."
                className="glass-input min-h-[80px]"
              />
            </CardContent>
          </Card>

          {/* Bento Grid Sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="glass-card">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-heading text-lg flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-blue-900" />
                  1. Bienvenida
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <Textarea
                  name="bienvenida"
                  value={formData.bienvenida}
                  onChange={handleChange}
                  placeholder="Notas de la bienvenida..."
                  className="glass-input min-h-[80px]"
                />
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-heading text-lg flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5 text-blue-900" />
                  2. Revisión de Tareas
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <Textarea
                  name="revision_tareas"
                  value={formData.revision_tareas}
                  onChange={handleChange}
                  placeholder="¿Cómo fueron las tareas asignadas?"
                  className="glass-input min-h-[80px]"
                />
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-heading text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-900" />
                  3. Avances en el Objetivo
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <Textarea
                  name="avances_objetivo"
                  value={formData.avances_objetivo}
                  onChange={handleChange}
                  placeholder="¿Qué avances se han logrado?"
                  className="glass-input min-h-[80px]"
                />
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-heading text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-900" />
                  4-5. Tema de la Sesión
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <Textarea
                  name="tema_sesion"
                  value={formData.tema_sesion}
                  onChange={handleChange}
                  placeholder="Tema seleccionado para esta sesión..."
                  className="glass-input min-h-[80px]"
                />
              </CardContent>
            </Card>
          </div>

          {/* Estado Actual y Deseado */}
          <Card className="glass-card">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-heading text-lg flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-blue-900" />
                6. Estado Actual y Deseado
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-slate-600 font-medium">Estado Actual</Label>
                <Textarea
                  name="estado_actual"
                  value={formData.estado_actual}
                  onChange={handleChange}
                  placeholder="Describe la situación actual..."
                  className="glass-input min-h-[100px]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600 font-medium">Estado Deseado</Label>
                <Textarea
                  name="estado_deseado"
                  value={formData.estado_deseado}
                  onChange={handleChange}
                  placeholder="Describe el estado deseado..."
                  className="glass-input min-h-[100px]"
                />
              </div>
            </CardContent>
          </Card>

          {/* KPI y Exploración */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="glass-card">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-heading text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-900" />
                  7. KPI de la Sesión
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <Textarea
                  name="kpi_sesion"
                  value={formData.kpi_sesion}
                  onChange={handleChange}
                  placeholder="Key Performance Indicator de la sesión..."
                  className="glass-input min-h-[80px]"
                />
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-heading text-lg flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-blue-900" />
                  8. Preguntas Significativas
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <Textarea
                  name="preguntas_significativas"
                  value={formData.preguntas_significativas}
                  onChange={handleChange}
                  placeholder="Preguntas que resultaron significativas..."
                  className="glass-input min-h-[80px]"
                />
              </CardContent>
            </Card>
          </div>

          {/* Técnicas y Tareas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="glass-card">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-heading text-lg flex items-center gap-2">
                  <Award className="w-5 h-5 text-blue-900" />
                  9. Técnicas Utilizadas
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <Textarea
                  name="tecnicas_utilizadas"
                  value={formData.tecnicas_utilizadas}
                  onChange={handleChange}
                  placeholder="Rueda de la vida, Niveles lógicos..."
                  className="glass-input min-h-[80px]"
                />
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-heading text-lg flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5 text-blue-900" />
                  10. Tareas para Próxima Sesión
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <Textarea
                  name="tareas_proxima_sesion"
                  value={formData.tareas_proxima_sesion}
                  onChange={handleChange}
                  placeholder="Tareas asignadas al coachee..."
                  className="glass-input min-h-[80px]"
                />
              </CardContent>
            </Card>
          </div>

          {/* Conclusiones */}
          <Card className="glass-card">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-heading text-lg flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-blue-900" />
                11. Conclusiones y Aprendizajes
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <Textarea
                name="conclusiones_aprendizajes"
                value={formData.conclusiones_aprendizajes}
                onChange={handleChange}
                placeholder="Principales conclusiones de la sesión..."
                className="glass-input min-h-[100px]"
              />
            </CardContent>
          </Card>

          {/* Revisión KPI */}
          <Card className="glass-card">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-heading text-lg flex items-center gap-2">
                <CheckSquare className="w-5 h-5 text-blue-900" />
                12. Revisión de KPI
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <Textarea
                name="revision_kpi"
                value={formData.revision_kpi}
                onChange={handleChange}
                placeholder="¿Se alcanzó el KPI definido?"
                className="glass-input min-h-[80px]"
              />
            </CardContent>
          </Card>

          {/* Coach Section */}
          <Card className="glass-card border-2 border-sky-200">
            <CardHeader className="border-b border-slate-100 bg-sky-50/50">
              <CardTitle className="text-heading text-lg flex items-center gap-2 text-sky-800">
                <Award className="w-5 h-5" />
                Para el Coach
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-slate-400" />
                    Aprendizajes del Coach
                  </Label>
                  <Textarea
                    name="aprendizajes_coach"
                    value={formData.aprendizajes_coach}
                    onChange={handleChange}
                    placeholder="Aprendizajes generados..."
                    className="glass-input min-h-[80px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-slate-400" />
                    Fortaleza Desarrollada
                  </Label>
                  <Textarea
                    name="fortaleza_desarrollada"
                    value={formData.fortaleza_desarrollada}
                    onChange={handleChange}
                    placeholder="Fortaleza desarrollada..."
                    className="glass-input min-h-[80px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-slate-400" />
                    Área de Oportunidad
                  </Label>
                  <Textarea
                    name="area_oportunidad"
                    value={formData.area_oportunidad}
                    onChange={handleChange}
                    placeholder="Áreas a desarrollar..."
                    className="glass-input min-h-[80px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-slate-400" />
                    Preguntas al Meta Coach
                  </Label>
                  <Textarea
                    name="preguntas_metacoach"
                    value={formData.preguntas_metacoach}
                    onChange={handleChange}
                    placeholder="Preguntas para retroalimentación..."
                    className="glass-input min-h-[80px]"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4 pb-8">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate(-1)}
              className="btn-outline"
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              className="btn-primary"
              disabled={saving}
              data-testid="save-session-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Guardar Cambios
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
